import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  MenuItem,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { useDispatch } from "react-redux";
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import BookingApi from "Services/Booking";
import RideApi from "Services/Ride";

const BookingCreateModal = ({ openDialog, handleClose, bookingId = "" }) => {
  const dispatch = useDispatch();
  const [rides, setRides] = useState([]);
  const [waypoints, setWaypoints] = useState([]);
  const isEditMode = Boolean(bookingId);

  const normalizeList = (res, key) => {
    const payload = res?.data;
    if (Array.isArray(payload?.[key])) return payload[key] || [];
    if (Array.isArray(payload?.data?.[key])) return payload.data[key] || [];
    if (Array.isArray(payload?.data?.data)) return payload.data.data || [];
    if (Array.isArray(payload?.data?.bookings)) return payload.data.bookings || [];
    if (Array.isArray(payload?.data)) return payload.data || [];
    return [];
  };

  useEffect(() => {
    if (!openDialog) return;

    formik.resetForm();

    RideApi.getRideList({ page: 1, limit: 1000, search: "" }).then(
      (res) => setRides(normalizeList(res, "rides")),
      () => {
        setRides([]);
        dispatch(
          showSnackbar({
            message: "Unable to load rides",
            autoHideDuration: 2500,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "error",
          })
        );
      }
    );

    RideApi.getRideWaypointList({ page: 1, limit: "unlimited", search: "" }).then(
      (res) => setWaypoints(normalizeList(res, "waypoints")),
      () => setWaypoints([])
    );
  }, [openDialog]);

  useEffect(() => {
    if (!openDialog || !isEditMode || !bookingId) return;
    dispatch(showLoader("Loading please wait..."));
    BookingApi.getBookingById(bookingId).then(
      (res) => {
        dispatch(hideLoader());
        const data = res?.data?.data || res?.data || {};
        formik.setValues({
          ride_id: data?.ride_id || "",
          pickup_waypoint_id: data?.pickup_waypoint_id || "",
          dropoff_waypoint_id: data?.dropoff_waypoint_id || "",
          seats_booked: Number(data?.seats_booked ?? 1),
          total_amount: Number(data?.total_amount ?? 0),
          service_fee: Number(data?.service_fee ?? 0),
          currency: data?.currency || "XAF",
          status: data?.status || "pending",
        });
      },
      (err) => {
        dispatch(hideLoader());
        dispatch(
          showSnackbar({
            message: err?.response?.data?.message || "Failed to fetch booking details",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "error",
          })
        );
      }
    );
  }, [openDialog, isEditMode, bookingId, dispatch]);

  const formik = useFormik({
    initialValues: {
      ride_id: "",
      pickup_waypoint_id: "",
      dropoff_waypoint_id: "",
      seats_booked: 1,
      total_amount: 2500,
      service_fee: 250,
      currency: "XAF",
      status: "pending",
    },
    validationSchema: yup.object({
      ride_id: yup.string().required("Ride is required"),
      pickup_waypoint_id: yup.string().required("Pickup waypoint is required"),
      dropoff_waypoint_id: yup.string().required("Dropoff waypoint is required"),
      seats_booked: yup.number().required("Seats booked is required").min(1, "Minimum is 1"),
      total_amount: yup.number().required("Total amount is required").min(0, "Minimum is 0"),
      service_fee: yup.number().required("Service fee is required").min(0, "Minimum is 0"),
      currency: yup.string().required("Currency is required"),
      status: yup.string(),
    }),
    onSubmit: (values) => {
      const payload = {
        ride_id: values.ride_id,
        pickup_waypoint_id: values.pickup_waypoint_id,
        dropoff_waypoint_id: values.dropoff_waypoint_id,
        seats_booked: Number(values.seats_booked),
        total_amount: Number(values.total_amount),
        service_fee: Number(values.service_fee),
        currency: values.currency,
        status: values.status,
      };

      dispatch(showLoader("Loading please wait..."));
      const request = isEditMode ? BookingApi.updateBooking(bookingId, payload) : BookingApi.createBooking(payload);
      request.then(
        (res) => {
          dispatch(hideLoader());
          if (res?.status === 200 || res?.status === 201) {
            dispatch(
              showSnackbar({
                message: res?.data?.message || (isEditMode ? "Booking updated successfully" : "Booking created successfully"),
                autoHideDuration: 3000,
                anchorOrigin: { vertical: "top", horizontal: "right" },
                variant: "success",
              })
            );
            handleClose(true);
          }
        },
        (err) => {
          dispatch(hideLoader());
          dispatch(
            showSnackbar({
              message: err?.response?.data?.message || (isEditMode ? "Error while updating booking" : "Error while creating booking"),
              autoHideDuration: 3000,
              anchorOrigin: { vertical: "top", horizontal: "right" },
              variant: "error",
            })
          );
        }
      );
    },
  });

  const getRideId = (ride) => ride?.id || ride?._id || "";
  const getWaypointId = (wp) => wp?.id || wp?._id || "";

  const waypointOptions = waypoints || [];

  return (
    <Dialog open={openDialog} onClose={() => handleClose(false)} fullWidth maxWidth="sm">
      <DialogTitle>{isEditMode ? "Edit Booking" : "Add Booking"}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label="Ride"
              name="ride_id"
              value={formik.values.ride_id}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.ride_id && Boolean(formik.errors.ride_id)}
              helperText={formik.touched.ride_id && formik.errors.ride_id}
            >
              <MenuItem value="">Select Ride</MenuItem>
              {rides.map((ride) => {
                const rideId = getRideId(ride);
                if (!rideId) return null;
                const origin = ride?.places_rides_origin_place_idToplaces?.name || ride?.origin?.name || ride?.origin_place_id || "";
                const destination = ride?.places_rides_destination_place_idToplaces?.name || ride?.destination?.name || ride?.destination_place_id || "";
                const rideLabel = origin || destination ? `${origin || "-"} - ${destination || "-"}` : `Ride ${rideId}`;
                return (
                  <MenuItem key={rideId} value={rideId}>
                    {rideLabel}
                  </MenuItem>
                );
              })}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label="Pickup Waypoint"
              name="pickup_waypoint_id"
              value={formik.values.pickup_waypoint_id}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.pickup_waypoint_id && Boolean(formik.errors.pickup_waypoint_id)}
              helperText={formik.touched.pickup_waypoint_id && formik.errors.pickup_waypoint_id}
            >
              <MenuItem value="">Select Pickup Waypoint</MenuItem>
              {waypointOptions.map((wp) => {
                const wpId = getWaypointId(wp);
                if (!wpId) return null;
                const label = `${wp?.places?.name || wp?.place_id || wpId} (Seq: ${wp?.sequence_order ?? 0})`;
                return (
                  <MenuItem key={wpId} value={wpId}>
                    {label}
                  </MenuItem>
                );
              })}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label="Dropoff Waypoint"
              name="dropoff_waypoint_id"
              value={formik.values.dropoff_waypoint_id}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.dropoff_waypoint_id && Boolean(formik.errors.dropoff_waypoint_id)}
              helperText={formik.touched.dropoff_waypoint_id && formik.errors.dropoff_waypoint_id}
            >
              <MenuItem value="">Select Dropoff Waypoint</MenuItem>
              {waypointOptions.map((wp) => {
                const wpId = getWaypointId(wp);
                if (!wpId) return null;
                const label = `${wp?.places?.name || wp?.place_id || wpId} (Seq: ${wp?.sequence_order ?? 0})`;
                return (
                  <MenuItem key={wpId} value={wpId}>
                    {label}
                  </MenuItem>
                );
              })}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Seats Booked"
              name="seats_booked"
              value={formik.values.seats_booked}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.seats_booked && Boolean(formik.errors.seats_booked)}
              helperText={formik.touched.seats_booked && formik.errors.seats_booked}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Total Amount"
              name="total_amount"
              value={formik.values.total_amount}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.total_amount && Boolean(formik.errors.total_amount)}
              helperText={formik.touched.total_amount && formik.errors.total_amount}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Service Fee"
              name="service_fee"
              value={formik.values.service_fee}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.service_fee && Boolean(formik.errors.service_fee)}
              helperText={formik.touched.service_fee && formik.errors.service_fee}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Currency"
              name="currency"
              value={formik.values.currency}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.currency && Boolean(formik.errors.currency)}
              helperText={formik.touched.currency && formik.errors.currency}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label="Status"
              name="status"
              value={formik.values.status}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            >
              <MenuItem value="pending">pending</MenuItem>
              <MenuItem value="confirmed">confirmed</MenuItem>
              <MenuItem value="cancelled">cancelled</MenuItem>
              <MenuItem value="completed">completed</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => handleClose(false)}>Cancel</Button>
        <Button variant="contained" onClick={formik.handleSubmit}>{isEditMode ? "Update" : "Add"}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default BookingCreateModal;
